import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import type { CustomerRegistrationInput } from '@/graphql/api'
import type { Email } from '@/types/email'
import { useRegionPlugin } from '@/plugins/region'
import { useRoutes } from '@/router/routes'
import { useAbsoluteUrl } from './app/useAbsoluteUrl'
import { getPhoneNumber } from '@/types/phoneNumber'
import { useCurrencyInput } from '@/plugins/currency-input.ts'

type NamesApi =
    | { first: string; last: string | null }
    | { first: ''; last: null }

export const useCustomerRegistrationInputStore = defineStore(
    'customerRegistrationInput',
    () => {
        const currency = useCurrencyInput()
        const { regionCode } = useRegionPlugin()
        const routes = useRoutes()
        const absoluteUrl = useAbsoluteUrl()

        const names = ref('')
        const email = ref<string>('')
        const validatedEmail = ref<Email | ''>('')
        const phoneNumberCountryCode = ref<string>('')
        const phoneNumber = ref<string>('')
        const emailConsent = ref(false)
        const googleSignInToken = ref<string | null>(null)
        const facebookSignInToken = ref<string | null>(null)
        const appleSignInToken = ref<string | null>(null)
        const forgottenPasswordUrl = absoluteUrl(
            routes.forgottenPassword()
        ).href

        const namesApi = computed((): NamesApi => {
            const pieces = names.value.trim().split(' ').filter(Boolean)

            if (pieces[0] === undefined) {
                return {
                    first: '',
                    last: null,
                }
            }

            if (pieces.length === 1 && pieces[0] !== undefined) {
                return {
                    first: pieces[0],
                    last: null,
                }
            }

            const last = pieces.pop()
            const first = pieces.join(' ')

            return {
                first,
                last: last || null,
            }
        })

        const customerRegistrationInput = computed(
            (): CustomerRegistrationInput | undefined => {
                const e64PhoneNumber = getPhoneNumber(
                    phoneNumberCountryCode.value,
                    phoneNumber.value
                )

                if (facebookSignInToken.value) {
                    return {
                        byFacebook: {
                            accessToken: facebookSignInToken.value,
                            region: regionCode.value,
                            currency: currency.value,
                            phone: e64PhoneNumber,
                            forgottenPasswordUrl,
                        },
                    }
                } else if (googleSignInToken.value) {
                    return {
                        byGoogle: {
                            token: googleSignInToken.value,
                            region: regionCode.value,
                            currency: currency.value,
                            phone: e64PhoneNumber,
                            forgottenPasswordUrl,
                        },
                    }
                } else if (appleSignInToken.value) {
                    return {
                        byApple: {
                            code: appleSignInToken.value,
                            region: regionCode.value,
                            currency: currency.value,
                            phone: e64PhoneNumber,
                            forgottenPasswordUrl,
                            firstName: namesApi.value.first,
                            lastName: namesApi.value.first,
                        },
                    }
                } else {
                    if (!validatedEmail.value) {
                        return undefined
                    }

                    return {
                        byManual: {
                            firstName: namesApi.value.first,
                            lastName: namesApi.value.last,
                            email: validatedEmail.value,
                            phone: e64PhoneNumber,
                            region: regionCode.value,
                            currency: currency.value,
                            emailConsent: emailConsent.value,
                            privacyConsent: true,
                            forgottenPasswordUrl,
                        },
                    }
                }
            }
        )

        return {
            names,
            email,
            validatedEmail,
            phoneNumberCountryCode,
            phoneNumber,
            emailConsent,
            googleSignInToken,
            facebookSignInToken,
            appleSignInToken,
            customerRegistrationInput,
        }
    }
)
